import { useCallback, useEffect, useRef, useState } from 'react';

// Shows a right-edge fade only while a scroll row actually has more content to the right.
// Uses a callback ref so it still attaches on pages that render a loading view first.
export function useOverflowFade() {
  const nodeRef = useRef(null);
  const cleanupRef = useRef(null);
  const [faded, setFaded] = useState(false);

  const update = useCallback(() => {
    const el = nodeRef.current;
    if (!el) return;
    setFaded(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
  }, []);

  const ref = useCallback((el) => {
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; }
    nodeRef.current = el;
    if (!el) { setFaded(false); return; }
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    Array.from(el.children).forEach(c => ro.observe(c));
    cleanupRef.current = () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro.disconnect();
    };
    update();
  }, [update]);

  useEffect(() => () => { if (cleanupRef.current) cleanupRef.current(); }, []);

  return { ref, faded };
}
